import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Loader2 } from "lucide-react";
import { verifyMember } from "../lib/api";

const REASON_UI = {
  active: {
    icon: CheckCircle2,
    color: "text-emerald-600",
    bg: "bg-emerald-50 border-emerald-200",
    label: "Carte ITERCATUS valide",
    badge: "bg-emerald-600 text-white",
  },
  expired: {
    icon: AlertTriangle,
    color: "text-amber-600",
    bg: "bg-amber-50 border-amber-200",
    label: "Carte expirée",
    badge: "bg-amber-500 text-white",
  },
  suspended: {
    icon: XCircle,
    color: "text-red-600",
    bg: "bg-red-50 border-red-200",
    label: "Carte non valide",
    badge: "bg-red-600 text-white",
  },
  pending: {
    icon: HelpCircle,
    color: "text-gray-600",
    bg: "bg-gray-50 border-gray-200",
    label: "Carte en attente",
    badge: "bg-gray-500 text-white",
  },
  not_found: {
    icon: XCircle,
    color: "text-gray-700",
    bg: "bg-gray-50 border-gray-200",
    label: "Carte non reconnue",
    badge: "bg-gray-700 text-white",
  },
};

export default function VerifyMemberPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await verifyMember(token);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: "Erreur de vérification" });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (state.error || !state.data) {
    const ui = REASON_UI.not_found;
    const Icon = ui.icon;
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${ui.bg}`}>
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border" data-testid="verify-error">
          <Icon className={`h-16 w-16 mx-auto ${ui.color}`} />
          <h1 className="mt-4 text-2xl font-semibold text-gray-900">Vérification impossible</h1>
          <p className="mt-2 text-sm text-gray-600">Réessayez plus tard ou scannez à nouveau la carte.</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const ui = REASON_UI[d.reason] || REASON_UI.not_found;
  const Icon = ui.icon;
  const displayName = [d.first_name, d.last_name].filter(Boolean).join(" ");

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${ui.bg}`} data-testid="verify-member-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border">
        <div className="p-8 text-center">
          <Icon className={`h-20 w-20 mx-auto ${ui.color}`} data-testid="verify-status-icon" />
          <h1 className="mt-4 text-2xl font-semibold text-gray-900" data-testid="verify-status-label">
            {ui.label}
          </h1>
          <div className={`mt-3 inline-block px-3 py-1 rounded-full text-xs font-medium ${ui.badge}`}>
            {d.member_type_label || "ITERCATUS"}
          </div>
        </div>

        <div className="border-t px-8 py-6 space-y-3 bg-gray-50">
          {displayName ? (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Adhérent</span>
              <span className="font-medium text-gray-900" data-testid="verify-name">{displayName}</span>
            </div>
          ) : null}
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Numéro</span>
            <span className="font-mono text-gray-900" data-testid="verify-number">{d.member_number || "—"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Validité</span>
            <span className="text-gray-900 text-right" data-testid="verify-validity">{d.validity_label || "—"}</span>
          </div>
        </div>

        <div className="px-8 py-4 text-center text-xs text-gray-500 border-t">
          Association ITERCATUS · vérification instantanée
        </div>
      </div>
    </div>
  );
}
